import React, { useState, useEffect } from 'react';
import { startCalibration, stopCalibration } from './ble';

const CalibrationItem = ({
  rawCalibrationData,
  setRawCalibrationData,
  filename,
  downloadAutomatically,
  initialCalibrationAngle,
  calibrationStep,
  calibrationCount,
  onComplete,
  onAngleChange // New prop for angle change callback
}) => {
  const [calibrationAngle, setCalibrationAngle] = useState(initialCalibrationAngle);
  const [totalCounter, setTotalCounter] = useState(0);
  const [currentAngleCounter, setCurrentAngleCounter] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [timerId, setTimerId] = useState(null);
  const [latestValue, setLatestValue] = useState(null);
  const [isCalibrationActive, setIsCalibrationActive] = useState(false);
  const [originalTitle, setOriginalTitle] = useState('');
  const [titleSet, setTitleSet] = useState(false);

  const totalCalibrations = calibrationStep ? (360 / calibrationStep * calibrationCount) : calibrationCount;

  const setTitle = (newTitle) => {
    if (!titleSet) {
      setOriginalTitle(document.title);
      setTitleSet(true);
    }
    document.title = newTitle;
  };

  const resetTitle = (reason) => {
    if (titleSet) {
      document.title = originalTitle;
      console.log(`Original title restored because ${reason}.`);
      setTitleSet(false);
    }
  };

  useEffect(() => {
    let timer;
    if (startTime) {
      timer = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      setTimerId(timer);
    }
    return () => clearInterval(timer);
  }, [startTime]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && titleSet) {
        resetTitle('the page gained focus');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [titleSet, originalTitle]);

  useEffect(() => {
    if (isCalibrationActive) {
      updatePageTitle();
    }
  }, [elapsedTime, totalCounter, isCalibrationActive]);

  useEffect(() => {
    if (totalCounter >= totalCalibrations) {
      clearInterval(timerId);
      setIsCalibrationActive(false);
      if (document.hidden) {
        setTitle(`❗ ${originalTitle}`);
        console.log('Exclamation mark set because calibration ended while page was out of focus.');
      } else {
        resetTitle('calibration ended and page was in focus');
      }
      if (onComplete) onComplete();
      if (downloadAutomatically) handleDownloadRaw();
    }
  }, [totalCounter]);

  const handleStartCalibration = async () => {
    console.log('Starting calibration with angle:', calibrationAngle);
    setStartTime(Date.now());
    setTotalCounter(rawCalibrationData.length); // Set totalCounter to the number of items in rawCalibrationData
    setIsCalibrationActive(true);
    await startCalibration(calibrationAngle, handleCalibrationData);
  };

  const handleStopCalibration = async () => {
    console.log('Stopping calibration');
    await stopCalibration();
    setIsCalibrationActive(false);
    clearInterval(timerId);
    resetTitle('calibration was manually stopped');
  };

  const handleCalibrationData = (data) => {
    console.log('Received data item:', data); // Log each received data item

    const { angle = 0, x = 0, y = 0, z = 0 } = data;

    setRawCalibrationData(prevData => [...prevData, { angle, x, y, z }]);
    setTotalCounter(prevCounter => prevCounter + 1);
    setLatestValue(data);

    setCurrentAngleCounter(prevCounter => {
      const newCounter = prevCounter + 1;
      if (angle === -1 || newCounter >= calibrationCount) {
        if (!calibrationStep || calibrationStep === 0) {
          // If no step, consider calibration complete
          handleStopCalibration();
          if (onComplete) onComplete();
          return 0;
        }
        setCalibrationAngle(prevAngle => {
          const nextAngle = prevAngle + calibrationStep;
          if (nextAngle >= 360) {
            handleStopCalibration();
            setCalibrationAngle(0);
            if (onComplete) onComplete();
          } else {
            startCalibration(nextAngle, handleCalibrationData);
            onAngleChange(nextAngle); // Call the callback to update the parent state
          }
          return nextAngle;
        });
        return 0;
      }
      return newCounter;
    });
  };

  const handleDownloadRaw = () => {
    console.log(rawCalibrationData);
    const csvContent = "data:text/csv;charset=utf-8,"
      + rawCalibrationData.map(e => `${e.x}, ${e.y}, ${e.z}, ${e.angle}`).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
  };

  const handleResetRaw = () => {
    console.log('Resetting raw data');
    setRawCalibrationData([]);
    setTotalCounter(0);
    setCurrentAngleCounter(0);
    setElapsedTime(0);
    setLatestValue(null);
    clearInterval(timerId);
    resetTitle('raw data was reset');
  };

  const calculateTimeRemaining = () => {
    const progress = totalCounter / totalCalibrations;
    if (progress === 0) {
      return -1;
    }
    const estimatedTotalTime = elapsedTime / progress;
    const remainingTime = Math.round(estimatedTotalTime - elapsedTime);
    return Math.max(remainingTime, 0);
  };

  const formatTime = (timeInSeconds) => {
    if (timeInSeconds === -1) {
      return 'calculating...';
    }
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  const updatePageTitle = () => {
    const remainingTime = formatTime(calculateTimeRemaining());
    const progressPercent = Math.round((totalCounter / totalCalibrations) * 100);
    setTitle(`⚙️ ${totalCounter}: ${progressPercent}% - ${remainingTime}`);
  };

  return (
    <div className="calibration-item">
      <button onClick={handleStartCalibration}>Start calibration</button>
      {isCalibrationActive && <button onClick={handleStopCalibration}>Stop calibration</button>}
      {(isCalibrationActive || rawCalibrationData.length > 0) && (
        <>
          <div className="progress-bar">
            <div className="progress" style={{ width: `${(totalCounter / totalCalibrations) * 100}%` }}>
              <span className="progress-time">
                Elapsed: {formatTime(elapsedTime)}, Remaining: {formatTime(calculateTimeRemaining())}
              </span>
            </div>
          </div>
          <div id="counter">
            Total row count: {totalCounter}/{totalCalibrations} : {Math.round((totalCounter / totalCalibrations) * 100)}%
          </div>
          <div id="current-angle-counter">Current angle row count: {currentAngleCounter}/{calibrationCount}</div>
          <div id="latest-value">Latest Value: {latestValue ? `X: ${latestValue.x}, Y: ${latestValue.y}, Z: ${latestValue.z}, Angle: ${latestValue.angle}` : 'N/A'}</div>
          <div>
            <button onClick={handleResetRaw}>Reset</button>
            <button onClick={handleDownloadRaw}>Download raw CSV</button>
          </div>
        </>
      )}
    </div>
  );
};

export default CalibrationItem;
